import { LookUpServiceAndValidation } from '../../../services/look-up-and-validation.service';
import { Component, Input, OnInit, OnChanges, SimpleChanges, Self } from '@angular/core';
import { ControlBaseComponent } from '../control-base/control-base.component';
import { environment } from '../../../../environments/environment';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Validators, NgControl, NgModel } from '@angular/forms';
import { StateService } from '../../../services/state.service';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';

@Component({
    selector: 'minster-tel',
    standalone: true,
    imports: [MatFormFieldModule, MatInputModule, MatIconModule, FormsModule],
    templateUrl: './minster-tel.component.html',
    styleUrl: './minster-tel.component.css'
})

export class MinsterTelComponent extends ControlBaseComponent<string> implements OnInit, OnChanges {
    override _minLength: number = 10;
    override _maxLength: number = 15;

    @Input() phoneType: 'mobile' | 'landline' = 'mobile';
    @Input() requiredPreSubmission: boolean = false;

    constructor(
        @Self() public controlDir: NgControl,
        private lookUpService: LookUpServiceAndValidation,
        private toastr: ToastrService,
        private stateService: StateService
    ) {
        super(controlDir, stateService);
    }
    
    ngOnInit(): void {
        this.setup();
        this.applyValidators();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['phoneType'] && !changes['phoneType'].firstChange) {
            this.applyValidators();
        }
    }

    valueUpdated(event: any): void {
        this._value = event;
        this._propagateChange(this._value);
        const control = this.controlDir.control;

        if (control) {
            if (this.requiredPreSubmission || this._required) {
                this._showValidationError = !this._value;
                control.setErrors(this._value ? null : { required: true });
            }
        }
    }

    onBlur(input: NgModel): void {
        const control = this.controlDir.control;

        if (!control) {
            return;
        }

        if (input.valid) {
            if (environment.production) {
                this.lookUpService.checkTelephone(this._value).subscribe(result => {
                    if (result.isValid) {
                        this.toastr.info(`${this._value} is a valid telephone number`);
                        control.setErrors(null);
                    } 
                    else {
                        this.toastr.error(`${this._value} is not a valid telephone number according to our validation rules.`);
                        control.setErrors({ invalidPhone: true });
                    }
                });
            }
        } 
        else {
            const hasNonRequiredError = input.errors && Object.keys(input.errors).some(key => key !== 'required');

            if (this.stateService.formIsSubmitted || this.requiredPreSubmission || hasNonRequiredError) {
                this._showValidationError = true;
                control.setErrors(input.errors);
            } 
            else {
                this._showValidationError = false;
                control.setErrors(null);
            }
        }
    }

    private applyValidators(): void {
        const control = this.controlDir.control;
        if (!control) return;

        const validators = [];

        if (this._required || this.requiredPreSubmission) {
            validators.push(Validators.required);
        }

        validators.push(Validators.minLength(this._minLength));
        validators.push(Validators.maxLength(this._maxLength));
        validators.push(Validators.pattern(this.phoneType === 'mobile' ? '^07[0-9]*$' : '^(?:01|02|03|08)[0-9]*$'));

        control.setValidators(validators);
        control.updateValueAndValidity();
    }

    get errorMessage(): string {
        const control = this.controlDir?.control;

        if (control?.errors) {
            if (control.errors['required'] && (this.stateService.formIsSubmitted || this.requiredPreSubmission)) {
                return 'This field is required';
            }
            if (control.errors['invalidPhone']) {
                return 'This is not a valid phone number';
            }
            if (control.errors['minlength']) {
                return `Phone number must be at least ${this._minLength} characters long`;
            }
            if (control.errors['maxlength']) {
                return `Phone number must not exceed ${this._maxLength} characters`;
            }
            if (control.errors['pattern']) {
                return this.phoneType === 'mobile' ? 'Invalid mobile number' : 'Invalid landline number';
            }
        }

        return this._errorText || 'Invalid input.';
    }
}